import React from 'react'
import styled from '@emotion/styled'
import { font, spacing } from '@mobi/component-library/Foundations'
import { InfoBox } from '@mobi/component-library/Feedback/InfoBox'
import type { BetSlipItem } from '@mobi/betslip/types'
import { BetCardTitle } from '@mobi/betslip/Components/Common/BetCardTitle'
import { calculateMultiReturn } from '@mobi/betslip/helpers/calculator/multis'
import {
  getBetsInMulti,
  getMultiLegEventOrRaceKey,
  hasTooFewMultiLegs,
  hasTooManyMultiLegs,
} from '@mobi/betslip/helpers/state'
import { useAppSelector } from '@mobi/betslip/Store/hooks'
import { MAX_LEGS_IN_RACE_MULTI, MIN_LEGS_IN_MULTI } from '@mobi/betslip/helpers/constants'
import { selectMultiBetError, selectMultiBetReceipt } from '@mobi/betslip/Store/Bets/selectors'
import { Price } from '@mobi/betslip/Components/Common/Price'
import { InvestmentSummary } from './Components/InvestmentSummary'
import { MultiItem } from './Components/MultiItem'

export const MultiItems: React.FC<{
  multiItems: BetSlipItem[]
  hasValidMultiInvestment: boolean
  isEditable: boolean
  appliedBonus?: number
  children?: React.ReactNode
}> = ({ multiItems, children, hasValidMultiInvestment, isEditable, appliedBonus }) => {
  const multiBetError = useAppSelector(selectMultiBetError)
  const multiBetReceipt = useAppSelector(selectMultiBetReceipt)

  const prevReturnRef = React.useRef<number | null>(null)
  const prevMultiPriceKeyRef = React.useRef<string | null>(null)

  const multiReturn = calculateMultiReturn(multiItems, { shouldRound: true }) || null

  React.useEffect(() => {
    prevReturnRef.current = multiReturn
  }, [multiReturn])

  const selectedMultiItems = getBetsInMulti(multiItems)
  const groupedItems = groupByEventOrRace(multiItems, isEditable)

  const isBelowMinLegCount = hasTooFewMultiLegs(selectedMultiItems)
  const isExceedingMaxLegCount = hasTooManyMultiLegs(selectedMultiItems)

  const maxLegCount = isExceedingMaxLegCount ? MAX_LEGS_IN_RACE_MULTI : 0

  const multiPriceKey = React.useMemo(() => {
    const key = selectedMultiItems.map(({ id, multiLegBetType }) => id + multiLegBetType).join('.')
    if (key !== prevMultiPriceKeyRef.current) prevReturnRef.current = null
    prevMultiPriceKeyRef.current = key
    return key
  }, [selectedMultiItems])

  return (
    <MultiContentStyled data-testid='Multi'>
      <BetCardTitle
        title={`${selectedMultiItems.length} Leg Multi`}
        rightComponent={
          multiReturn ? (
            <Price
              value={multiReturn}
              valueLastSeen={multiBetReceipt ? null : prevReturnRef.current}
              key={multiPriceKey}
              roundDown
            />
          ) : (
            'N/A'
          )
        }
      />

      {(isBelowMinLegCount || isExceedingMaxLegCount) && (
        <InfoBox color='orange' size='sm'>
          {isBelowMinLegCount &&
            `Minimum of ${MIN_LEGS_IN_MULTI} legs allowed for a Multi bet. Please review your bet.`}

          {isExceedingMaxLegCount &&
            `Maximum of ${maxLegCount} legs allowed for a Multi bet. Please review your bet.`}
        </InfoBox>
      )}

      {multiBetError?.errorMessage && (
        <InfoBox color='red' size='sm'>
          {multiBetError.errorMessage}
        </InfoBox>
      )}

      <section data-testid='Multi.Selections'>
        {Array.from(groupedItems).map(([groupId, items]) => (
          <MultiItem key={groupId} items={items} />
        ))}
      </section>

      {children}

      {!isEditable && hasValidMultiInvestment && (
        <InvestmentSummary selectedMultiItems={selectedMultiItems} appliedBonus={appliedBonus} />
      )}
    </MultiContentStyled>
  )
}

// =============
// Local Helpers
// =============

function groupByEventOrRace(items: BetSlipItem[], isEditable: boolean): GroupedItems {
  const groups: GroupedItems = new Map()

  return items.reduce((map: GroupedItems, item: BetSlipItem) => {
    if (!isEditable && !item.isInMulti) return map

    const groupKey = getMultiLegEventOrRaceKey(item.selectionDetails)
    if (!groupKey) return map.set(item.id, [item])

    if (map.has(groupKey)) {
      const currentGrouping = map.get(groupKey) as BetSlipItem[]
      map.set(groupKey, [...currentGrouping, item])
    } else {
      map.set(groupKey, [item])
    }

    return map
  }, groups)
}

// ======
// Styles
// ======

const MultiContentStyled = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: spacing.sm,
  fontFamily: font.family.primary,

  // Selections
  '> section': {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.sm,
  },
})

// =====
// Types
// =====

type GroupedItems = Map<string, BetSlipItem[]>
